import { redirect } from 'next/navigation';

import { getAppConfig } from '@services/config';
import {
    getLocalDataIfNotExpired,
    getLocalVariable,
    removeLocalVariable,
    setLocalVariable,
    setSessionVariable,
} from '@services/storage';

import { IRudiApiRechercherAdresse, LmPartners } from '@app-types/eligibility';

import { getClientCookies } from '@helpers/cookies-client';
import { replaceWindowUrl } from '@helpers/document';
import { paths } from '@helpers/path';

import { ELIGIBILITY_JOURNEY, ELIGIBILITY_STORAGE_KEY } from '@constants/provider';

import { JourneyConfigType } from '@eligibility/helpers';

import { getAccessKeyPartner, isReferrerValid } from './eligibility-referrer';

type SearchParamsReader = Pick<URLSearchParams, 'get' | 'toString'>;
type AppConfig = ReturnType<typeof getAppConfig>;

function getPathWithoutPartnerParams(searchParams: SearchParamsReader, pathname: string): string {
    const remainingParams = new URLSearchParams(searchParams.toString());
    remainingParams.delete('key');
    remainingParams.delete('partner');

    return `${pathname}${remainingParams.size > 0 ? '?' + remainingParams.toString() : ''}`;
}

function storePartnerReferrer(referrer: string, expirationDate: number) {
    setLocalVariable(
        ELIGIBILITY_STORAGE_KEY.LM_PARTNER,
        JSON.stringify({
            data: `https://${referrer}`,
            expiresTimestamp: expirationDate,
        }),
    );
}

function persistAccessKeyPartner(
    journeyConfig: JourneyConfigType,
    searchParams: SearchParamsReader,
    pathname: string,
    appConfig: AppConfig,
    expirationDate: number,
) {
    if (!journeyConfig.accessKeyPartnerConfigKey || journeyConfig.referrerConfigKey) {
        return;
    }

    const accessKeyPartner = getAccessKeyPartner(
        searchParams.get('key'),
        searchParams.get('partner'),
        appConfig[journeyConfig.accessKeyPartnerConfigKey] as LmPartners,
    );

    if (!accessKeyPartner) {
        removeLocalVariable(ELIGIBILITY_STORAGE_KEY.LM_PARTNER);
        redirect(paths.eligibilityTest());
    }

    storePartnerReferrer(accessKeyPartner.referrer, expirationDate);
    replaceWindowUrl(getPathWithoutPartnerParams(searchParams, pathname));
}

function persistReferrerPartner(
    journeyConfig: JourneyConfigType,
    searchParams: SearchParamsReader,
    pathname: string,
    appConfig: AppConfig,
    expirationDate: number,
) {
    if (!journeyConfig.referrerConfigKey) {
        return;
    }

    const referrerConfig = appConfig[journeyConfig.referrerConfigKey as keyof AppConfig] as string[];
    if (!referrerConfig?.length) {
        return;
    }

    const accessKeyPartner = journeyConfig.accessKeyPartnerConfigKey
        ? getAccessKeyPartner(
              searchParams.get('key'),
              searchParams.get('partner'),
              appConfig[journeyConfig.accessKeyPartnerConfigKey] as LmPartners,
          )
        : null;

    if (accessKeyPartner) {
        storePartnerReferrer(accessKeyPartner.referrer, expirationDate);
        replaceWindowUrl(getPathWithoutPartnerParams(searchParams, pathname));
        return;
    }

    if (!isReferrerValid(referrerConfig)) {
        removeLocalVariable(ELIGIBILITY_STORAGE_KEY.LM_PARTNER);
        redirect(paths.eligibilityTest());
    }

    if (getLocalDataIfNotExpired(ELIGIBILITY_STORAGE_KEY.LM_PARTNER) !== null) {
        return;
    }

    setLocalVariable(
        ELIGIBILITY_STORAGE_KEY.LM_PARTNER,
        JSON.stringify({
            data: document.referrer,
            expiresTimestamp: expirationDate,
        }),
    );
}

export function initializeEligibilityStorage(
    journeyConfig: JourneyConfigType,
    searchParams: SearchParamsReader,
    pathname: string,
    appConfig: AppConfig,
    expirationDate: number,
) {
    persistAccessKeyPartner(journeyConfig, searchParams, pathname, appConfig, expirationDate);
    persistReferrerPartner(journeyConfig, searchParams, pathname, appConfig, expirationDate);

    if (journeyConfig.referrerConfigKey && journeyConfig.name === ELIGIBILITY_JOURNEY.BANK) {
        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.YAMA_CART,
            JSON.stringify({
                data: true,
                expiresTimestamp: expirationDate,
            }),
        );
    }

    if (journeyConfig.cookieKey && !getClientCookies(journeyConfig.cookieKey)) {
        redirect(paths.eligibilityTest());
    }

    const url =
        journeyConfig.name === ELIGIBILITY_JOURNEY.EXTRA_BBOX
            ? paths.eligibilityTest()
            : globalThis.location.pathname +
              globalThis.location.search.replace('gamme=', 'focus=').replace('panierMixte=false', 'panierMixte=true');
    setLocalVariable(
        ELIGIBILITY_STORAGE_KEY.REFERER,
        JSON.stringify({
            data: url,
            expiresTimestamp: expirationDate,
        }),
    );
}

export function getStoredGcpData(): IRudiApiRechercherAdresse {
    return JSON.parse(getLocalVariable('eligibility_gcp_data') ?? '{}');
}

export function removeStoredGcpData() {
    removeLocalVariable('eligibility_gcp_data');
}

export function setThreeClicsStorage(active: boolean) {
    setSessionVariable('ftth3Clics', JSON.stringify({ data: active }));
}

export function setMixedCartStorage(active: boolean) {
    setSessionVariable('panierMixte', JSON.stringify({ data: active }));
}
